import { gql, useQuery } from '@apollo/client';
import { CurrencyCodes } from '@generated/graphql';
import type { AccountTypes } from '@interfaces/account';
import { AccountTypesWithDeregFees } from '@interfaces/account';
import { Box } from '@ovComponents/1-primative/box/box';
import { Typography } from '@ovComponents/1-primative/typography/typography';
import { Card, CardContent } from '@ovComponents/2-component/card/card';
import { formatMoneyValue } from '@utils/moneyFormatting';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useExchangeRate } from './useExchangeRate';
import {
  EXCEEDS_AVAILABLE_THRESHOLD,
  FEE_BASE_CURRENCY,
  FULL_WITHDRAWAL_THRESHOLD,
  ILLIQUID_PORTFOLIO_ESTIMATE_SPREAD,
} from './withdrawal.constants';

export const CALCULATE_WITHDRAWAL_FEES = gql`
  query calculateWithdrawFees($input: CalculateWithdrawFeesInput!) {
    calculateWithdrawFees(input: $input) {
      withdrawFees {
        type
        amountCents
      }
    }
  }
`;

export type MultiCurrencyAmount = {
  currency: CurrencyCodes;
  availableFundsToWithdrawCents: number;
  moneyAvailableCents?: number;
  pendingWithdrawCents?: number;
};

type WithdrawFee = {
  type: string;
  amountCents: number;
};

type CalculateWithdrawFeesResponse = {
  calculateWithdrawFees: {
    withdrawFees: WithdrawFee[];
  };
};

type Props = {
  accountId: string;
  accountType?: AccountTypes;
  availableToWithdrawCents: number;
  withdrawAmountCents: number;
  isIlliquidWithdrawal?: boolean;
  federalTaxWithheld?: number;
  stateTaxWithheld?: number;
  isMultiCurrencyAccount?: boolean;
  bankAccountCurrency?: CurrencyCodes;
  multiCurrencyAmounts?: MultiCurrencyAmount[];
  organizationId?: string;
};

type FeesCoverage = {
  showAllFeesInCad: boolean;
  partiallyCoveredFees: WithdrawFee[];
  cadMoneyAvailable: number;
};

/**
 * Calculates which fees can be covered by CAD balance for multi-currency accounts
 * Returns details about partial or full fee coverage
 */
const calculateFeesCoverage = (
  accountType: AccountTypes | undefined,
  isMultiCurrencyAccount: boolean,
  bankAccountCurrency: CurrencyCodes | undefined,
  multiCurrencyAmounts: MultiCurrencyAmount[],
  withdrawalFeesInCad: WithdrawFee[],
): FeesCoverage => {
  const defaultResult: FeesCoverage = {
    showAllFeesInCad: false,
    partiallyCoveredFees: [],
    cadMoneyAvailable: 0,
  };

  // Only applies to account types with dereg fees
  if (!accountType || !AccountTypesWithDeregFees.includes(accountType)) {
    return defaultResult;
  }

  // Only for multi-currency accounts withdrawing non-CAD
  if (!isMultiCurrencyAccount || bankAccountCurrency === CurrencyCodes.Cad) {
    return defaultResult;
  }

  // Check if there's CAD balance
  const cadBalance = multiCurrencyAmounts.find(
    (amount) => amount.currency === CurrencyCodes.Cad,
  );

  if (!cadBalance) {
    return defaultResult;
  }

  const cadMoneyAvailable = cadBalance?.moneyAvailableCents ?? 0;

  if (cadMoneyAvailable <= 0 || withdrawalFeesInCad.length === 0) {
    return defaultResult;
  }

  const totalFeesInCad = withdrawalFeesInCad.reduce(
    (sum, fee) => sum + fee.amountCents,
    0,
  );

  // All fees can be covered
  if (cadMoneyAvailable >= totalFeesInCad) {
    return {
      showAllFeesInCad: true,
      partiallyCoveredFees: [],
      cadMoneyAvailable,
    };
  }

  // Check which fees can be partially covered
  let remainingCadBalance = cadMoneyAvailable;
  const coveredFees: WithdrawFee[] = [];

  for (const fee of withdrawalFeesInCad) {
    if (remainingCadBalance >= fee.amountCents) {
      coveredFees.push(fee);
      remainingCadBalance -= fee.amountCents;
    }
  }

  return {
    showAllFeesInCad: false,
    partiallyCoveredFees: coveredFees,
    cadMoneyAvailable,
  };
};

const WithdrawalBreakdown = ({
  availableToWithdrawCents,
  accountId,
  accountType,
  withdrawAmountCents,
  isIlliquidWithdrawal,
  federalTaxWithheld,
  stateTaxWithheld,
  isMultiCurrencyAccount = false,
  bankAccountCurrency,
  multiCurrencyAmounts = [],
  organizationId,
}: Props): JSX.Element => {
  const { t } = useTranslation(['workflowCompletions', 'transfer']);

  const [totalFeesInCad, setTotalFeesInCad] = useState(0);
  const [totalTaxes, setTotalTaxes] = useState(0);
  const [fetchingFees, setFetchingFees] = useState(false);
  const [withdrawalFeesInCad, setWithdrawalFeesInCad] = useState<WithdrawFee[]>(
    [],
  );

  const {
    rate,
    isReady,
    hasRealRate,
    convertToBaseCurrency,
    convertFromBaseCurrency,
  } = useExchangeRate({
    organizationId,
    targetCurrency: bankAccountCurrency,
    baseCurrency: FEE_BASE_CURRENCY,
  });

  const feesCoverage = useMemo(
    () =>
      calculateFeesCoverage(
        accountType,
        isMultiCurrencyAccount,
        bankAccountCurrency,
        multiCurrencyAmounts,
        withdrawalFeesInCad,
      ),
    [
      accountType,
      isMultiCurrencyAccount,
      bankAccountCurrency,
      multiCurrencyAmounts,
      withdrawalFeesInCad,
    ],
  );

  const showFeesInCad = feesCoverage.showAllFeesInCad;

  const withdrawAmountInCadCents = useMemo(
    () => (isReady ? convertToBaseCurrency(withdrawAmountCents) : 0),
    [withdrawAmountCents, isReady, convertToBaseCurrency],
  );

  const displayFees = useMemo(() => {
    if (showFeesInCad) {
      return withdrawalFeesInCad;
    }

    // Skip conversion if:
    // - No bank account currency specified
    // - Bank account is in CAD (fees are already in CAD)
    // - Rate is not ready (still loading)
    // - We don't have a real fetched rate (only fallback default)
    const shouldSkipConversion =
      !bankAccountCurrency ||
      bankAccountCurrency === CurrencyCodes.Cad ||
      rate <= 0 ||
      !hasRealRate;

    if (shouldSkipConversion) {
      return withdrawalFeesInCad;
    }

    // Check if partial coverage - some fees in CAD, some converted
    if (feesCoverage.partiallyCoveredFees.length > 0) {
      const coveredFeeTypes = new Set(
        feesCoverage.partiallyCoveredFees.map((f) => f.type),
      );

      return withdrawalFeesInCad.map((fee) => {
        // If fee is covered by CAD, keep it in CAD (don't convert)
        if (coveredFeeTypes.has(fee.type)) {
          return fee;
        }
        // Otherwise, convert to bank account currency
        return {
          type: fee.type,
          amountCents: convertFromBaseCurrency(fee.amountCents),
        };
      });
    }

    // No fees covered by CAD - convert all to bank account currency
    return withdrawalFeesInCad.map((fee) => ({
      type: fee.type,
      amountCents: convertFromBaseCurrency(fee.amountCents),
    }));
  }, [
    withdrawalFeesInCad,
    showFeesInCad,
    bankAccountCurrency,
    rate,
    hasRealRate,
    convertFromBaseCurrency,
    feesCoverage.partiallyCoveredFees,
  ]);

  const totalFeesDisplay = useMemo(
    () => displayFees.reduce((sum, fee) => sum + fee.amountCents, 0),
    [displayFees],
  );

  // Check if FX rate is needed but not available
  // This happens when bank currency is non-CAD and we don't have a real rate
  const isFxRateMissing = useMemo(() => {
    // No issue if fees should be shown in CAD (multi-currency with sufficient CAD)
    if (showFeesInCad) return false;

    // No issue if bank is CAD or not specified
    if (!bankAccountCurrency || bankAccountCurrency === CurrencyCodes.Cad) {
      return false;
    }

    // FX rate is needed but not available
    return !hasRealRate && withdrawalFeesInCad.length > 0;
  }, [showFeesInCad, bankAccountCurrency, hasRealRate, withdrawalFeesInCad]);

  // Calculate fees to deduct from withdrawal amount
  // Excludes fees covered by CAD balance
  const feesToDeductFromWithdrawal = useMemo(() => {
    // If all fees are in CAD, don't deduct any
    if (showFeesInCad) return 0;

    // If FX rate is missing, can't calculate properly
    if (isFxRateMissing) return 0;

    // If some fees are covered by CAD, exclude them
    if (feesCoverage.partiallyCoveredFees.length > 0) {
      const coveredFeeTypes = new Set(
        feesCoverage.partiallyCoveredFees.map((f) => f.type),
      );

      return displayFees
        .filter((fee) => !coveredFeeTypes.has(fee.type))
        .reduce((sum, fee) => sum + fee.amountCents, 0);
    }

    // Otherwise, deduct all fees
    return totalFeesDisplay;
  }, [
    showFeesInCad,
    isFxRateMissing,
    feesCoverage.partiallyCoveredFees,
    displayFees,
    totalFeesDisplay,
  ]);

  const currencyLabel = useMemo(() => {
    // Multi-currency account with sufficient CAD balance: charged from CAD
    if (showFeesInCad) {
      return ` ${CurrencyCodes.Cad}`;
    }

    // No bank currency specified or bank is CAD: no label needed
    if (!bankAccountCurrency || bankAccountCurrency === CurrencyCodes.Cad) {
      return '';
    }

    // If we have a real rate, values are converted to bank currency
    if (hasRealRate) {
      return ` ${bankAccountCurrency}`;
    }

    return '';
  }, [showFeesInCad, bankAccountCurrency, hasRealRate]);

  const handleFeesData = useCallback(
    (data: CalculateWithdrawFeesResponse): void => {
      // Store fees in CAD (as received from API)
      const feesInCad = data.calculateWithdrawFees.withdrawFees;
      const totalCad = feesInCad.reduce((sum, fee) => sum + fee.amountCents, 0);

      setWithdrawalFeesInCad(feesInCad);
      setTotalFeesInCad(totalCad);
      setFetchingFees(false);
    },
    [],
  );

  const { loading } = useQuery<CalculateWithdrawFeesResponse>(
    CALCULATE_WITHDRAWAL_FEES,
    {
      variables: {
        input: {
          accountId,
          amountCents: withdrawAmountInCadCents,
          isfullWithdraw:
            withdrawAmountCents >=
            availableToWithdrawCents * FULL_WITHDRAWAL_THRESHOLD,
        },
      },
      skip: !accountId || !isReady || withdrawAmountInCadCents <= 0,
      onCompleted: handleFeesData,
      onError: () => setFetchingFees(false),
      nextFetchPolicy: 'standby',
    },
  );

  useEffect(() => {
    if (loading) {
      setFetchingFees(true);
    }
  }, [loading]);

  useEffect(() => {
    const total = (federalTaxWithheld ?? 0) + (stateTaxWithheld ?? 0);

    setTotalTaxes(total);
  }, [federalTaxWithheld, stateTaxWithheld]);

  const breakdownItems = (
    children: JSX.Element,
    value: string,
    key?: string,
    pl?: number,
  ): JSX.Element => (
    <Box
      key={key}
      display='flex'
      justifyContent='space-between'
      alignItems='center'
      mb={2}
      pl={pl ?? 0}
    >
      <Box display='flex' alignItems='center'>
        {children}
      </Box>
      <Typography variant='bodyLarge' weight='bold'>
        {value}
      </Typography>
    </Box>
  );

  return (
    <Card variant='outlined'>
      <CardContent>
        {breakdownItems(
          <Typography variant='bodyLarge'>
            {t('transfer:withdrawal.withdrawalBreakDown.availableToWithdraw')}
          </Typography>,
          isMultiCurrencyAccount
            ? ''
            : formatMoneyValue(availableToWithdrawCents, '$', 2),
        )}

        {isMultiCurrencyAccount &&
          multiCurrencyAmounts.map((multiCurrencyAmount) =>
            breakdownItems(
              <Typography variant='bodyLarge'>
                {multiCurrencyAmount.currency}
              </Typography>,
              formatMoneyValue(
                multiCurrencyAmount.availableFundsToWithdrawCents,
                '$',
                2,
              ),
              multiCurrencyAmount.currency,
              2,
            ),
          )}

        {breakdownItems(
          <Typography variant='bodyLarge'>
            {t('transfer:withdrawal.withdrawalBreakDown.withdrawalAmount')}
          </Typography>,
          formatMoneyValue(withdrawAmountCents, '$', 2),
        )}

        {!isFxRateMissing &&
          displayFees.map((withdrawalFee) => {
            // Check if this specific fee is covered by CAD balance
            const isCoveredByCad =
              showFeesInCad ||
              feesCoverage.partiallyCoveredFees.some(
                (f) => f.type === withdrawalFee.type,
              );

            // Determine the currency label for this fee
            const feeCurrencyLabel = isCoveredByCad
              ? ` ${CurrencyCodes.Cad}`
              : currencyLabel;

            return breakdownItems(
              <Typography variant='bodyLarge'>
                {t(
                  `transfer:withdrawal.withdrawalBreakDown.feesAndTaxTypes.${withdrawalFee.type}`,
                )}
              </Typography>,
              fetchingFees
                ? t('transfer:withdrawal.withdrawalBreakDown.loadingMessage')
                : `${formatMoneyValue(withdrawalFee.amountCents, '$', 2)}${feeCurrencyLabel}`,
              withdrawalFee.type,
            );
          })}

        {isFxRateMissing && withdrawalFeesInCad.length > 0 && (
          <Box mb={2}>
            <Typography variant='labelSmall' colorVariant='variant'>
              {t('transfer:withdrawal.withdrawalBreakDown.fxRateUnavailable')}
            </Typography>
          </Box>
        )}

        {showFeesInCad && displayFees.length > 0 && (
          <Box mb={2}>
            <Typography variant='labelSmall' colorVariant='variant'>
              {t('transfer:withdrawal.withdrawalBreakDown.feesChargedFromCad')}
            </Typography>
          </Box>
        )}

        {!showFeesInCad &&
          feesCoverage.partiallyCoveredFees.length > 0 &&
          bankAccountCurrency && (
            <Box mb={2}>
              <Typography variant='labelSmall' colorVariant='variant'>
                {feesCoverage.partiallyCoveredFees.length === 1
                  ? t(
                      'transfer:withdrawal.withdrawalBreakDown.partialFeeCoveredFromCad',
                      {
                        feeType: t(
                          `transfer:withdrawal.withdrawalBreakDown.feesAndTaxTypes.${feesCoverage.partiallyCoveredFees[0].type}`,
                        ),
                        amount: formatMoneyValue(
                          feesCoverage.partiallyCoveredFees[0].amountCents,
                          '$',
                          2,
                        ),
                      },
                    )
                  : t(
                      'transfer:withdrawal.withdrawalBreakDown.partialFeesCoveredFromCad',
                      {
                        count: feesCoverage.partiallyCoveredFees.length,
                      },
                    )}
              </Typography>
            </Box>
          )}

        {federalTaxWithheld &&
          breakdownItems(
            <Typography variant='bodyLarge'>
              {t('transfer:withdrawal.withdrawalBreakDown.federalTaxWithheld')}
            </Typography>,
            formatMoneyValue(federalTaxWithheld, '$', 2),
          )}

        {stateTaxWithheld &&
          breakdownItems(
            <Typography variant='bodyLarge'>
              {t('transfer:withdrawal.withdrawalBreakDown.stateTaxWithheld')}
            </Typography>,
            formatMoneyValue(stateTaxWithheld, '$', 2),
          )}

        <hr />

        <Box display='flex' flexDirection='column'>
          <Typography variant='bodyLarge'>
            {t(
              'transfer:withdrawal.withdrawalBreakDown.estimatedWithdrawalAmount',
            )}
          </Typography>

          <Typography variant='headingLarge'>
            {!isIlliquidWithdrawal ? (
              <>
                {formatMoneyValue(
                  withdrawAmountCents - feesToDeductFromWithdrawal - totalTaxes,
                  '$',
                  2,
                )}
                {isMultiCurrencyAccount && bankAccountCurrency
                  ? ` ${bankAccountCurrency}`
                  : ''}
              </>
            ) : (
              <>
                {formatMoneyValue(
                  withdrawAmountCents *
                    (1 - ILLIQUID_PORTFOLIO_ESTIMATE_SPREAD) -
                    feesToDeductFromWithdrawal -
                    totalTaxes,
                  '$',
                  2,
                )}
                {' - '}
                {formatMoneyValue(
                  withdrawAmountCents *
                    (1 + ILLIQUID_PORTFOLIO_ESTIMATE_SPREAD) -
                    feesToDeductFromWithdrawal -
                    totalTaxes,
                  '$',
                  2,
                )}
              </>
            )}
          </Typography>

          {showFeesInCad && bankAccountCurrency && (
            <Typography variant='labelSmall' colorVariant='variant' mt={1}>
              {t(
                'transfer:withdrawal.withdrawalBreakDown.feesDeductedSeparately',
                {
                  amount: formatMoneyValue(totalFeesInCad, '$', 2),
                  currency: CurrencyCodes.Cad,
                },
              )}
            </Typography>
          )}

          {availableToWithdrawCents &&
          withdrawAmountCents >
            (availableToWithdrawCents || 0) * EXCEEDS_AVAILABLE_THRESHOLD ? (
            <Typography variant='labelSmall' color='error' mt={1}>
              {t('workflowCompletions:depositReview.amountExceedsWithdraw')}
            </Typography>
          ) : (
            <span></span>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default WithdrawalBreakdown;
